import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playlist, setPlaylist] = useState([]);
  const [volume, setVolume] = useState(80);
  const audioRef = useRef(new Audio());

  // Apply volume changes to the audio element
  useEffect(() => {
    audioRef.current.volume = volume / 100;
  }, [volume]);

  const playTrack = useCallback((track, trackList) => {
    // If a track list is provided, set it as the playlist
    if (trackList && trackList.length > 0) {
      setPlaylist(trackList);
    }

    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => setIsPlaying(true)).catch(err => {
            console.warn('Play failed:', err);
          });
        } else {
          setIsPlaying(true);
        }
      }
    } else {
      audioRef.current.src = track.audioUrl;
      setCurrentTrack(track);
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => setIsPlaying(true)).catch(err => {
          console.warn('Play failed:', err);
        });
      } else {
        setIsPlaying(true);
      }
    }
  }, [currentTrack, isPlaying]);

  const pause = () => {
    audioRef.current.pause();
    setIsPlaying(false);
  };

  const play = () => {
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setIsPlaying(true)).catch(err => console.warn('Play failed:', err));
    } else {
      setIsPlaying(true);
    }
  };

  const seek = (time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const skipNext = useCallback(() => {
    if (!playlist.length || !currentTrack) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    const nextIndex = (currentIndex + 1) % playlist.length;
    const nextTrack = playlist[nextIndex];
    if (nextTrack?.audioUrl) {
      audioRef.current.src = nextTrack.audioUrl;
      setCurrentTrack(nextTrack);
      const p = audioRef.current.play();
      if (p !== undefined) p.then(() => setIsPlaying(true)).catch(err => console.warn('Play failed:', err));
      else setIsPlaying(true);
    }
  }, [playlist, currentTrack]);

  const skipPrev = useCallback(() => {
    // If more than 3 seconds in, restart current track
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    
    if (!playlist.length || !currentTrack) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    const prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
    const prevTrack = playlist[prevIndex];
    if (prevTrack?.audioUrl) {
      audioRef.current.src = prevTrack.audioUrl;
      setCurrentTrack(prevTrack);
      const p = audioRef.current.play();
      if (p !== undefined) p.then(() => setIsPlaying(true)).catch(err => console.warn('Play failed:', err));
      else setIsPlaying(true);
    }
  }, [playlist, currentTrack]);

  React.useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      // Auto-play next track
      if (playlist.length > 0 && currentTrack) {
        const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
        const nextIndex = currentIndex + 1;
        if (nextIndex < playlist.length) {
          const nextTrack = playlist[nextIndex];
          if (nextTrack?.audioUrl) {
            audio.src = nextTrack.audioUrl;
            setCurrentTrack(nextTrack);
            const p = audio.play();
            if (p !== undefined) p.then(() => setIsPlaying(true)).catch(err => console.warn('Auto-play failed:', err));
            else setIsPlaying(true);
            return;
          }
        }
      }
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [playlist, currentTrack]);

  return (
    <AudioContext.Provider value={{
      currentTrack,
      isPlaying,
      currentTime,
      duration,
      playlist,
      volume,
      setVolume,
      playTrack,
      pause,
      play,
      seek,
      skipNext,
      skipPrev,
      setPlaylist
    }}>
      {children}
    </AudioContext.Provider>
  );
};
